import React, { useEffect, useState, ReactElement } from 'react'
import styled from 'styled-components'

import IconChevronDown from '../IconChevronDown'
import { FirmwareListItem } from '../../lib/firmware-list-item'
import Modal from '../Modal'
import AlertModal from '../AlertModal'
import Text from '../Text'
import { Table, TableHeader, TableHeaderCell, TableRow } from '../../lib/table'
import { WidgetLayout } from '../../lib/widget-layout'
import { Button } from '../../common/components/Button'
import { LockIcon } from '../../lib/icons/lock-icon'
import { UnlockIcon } from '../../lib/icons/unlock-icon'
import useText from '../../common/hooks/useText'

import { IFirmware } from './UploadFirmwareTab'

export const SExpandIcon = styled(IconChevronDown)`
    fill: #424242;
    margin-left: 0.5em;
`

const SUnlockButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-top: 40px;
`

const SCollapseIcon = styled(SExpandIcon)`
    transform: rotate(180deg);
`

export const SExpandButton = styled.button`
    display: block;
    margin: 1em auto 0 auto;
    background: transparent;
    border: none;
    font-size: 16px;
    font-weight: 400;
    color: #424242;
`

const SSelectFirmwareVersionInstruction = styled.p`
    font-size: 16px;
    font-weight: 400;
`

const STitleWithIcon = styled.div`
    padding-right: 70px;
    width: 100%;
    position: relative;
`

export type FirmwareListProps = {
    data?: Array<IFirmware>
    onActivate?: (id: number) => void
    onRemove?: (id: number) => void
    onShowMore?: () => void
    onShowLess?: () => void
    showLessIsVisible?: boolean
    showMoreIsVisible?: boolean
    loading?: boolean
    showActionMenu?: boolean
}

export const FirmwareModalTable = () => {
    return (
        <TableHeader>
            <TableRow>
                <TableHeaderCell data-testid="version">
                    <Text code="lblVersion" />
                </TableHeaderCell>
                <TableHeaderCell data-testid="date">
                    <Text code="lblDate" />
                </TableHeaderCell>
                <TableHeaderCell data-testid="no-of-chargers">
                    <Text code="lblStatus" />
                </TableHeaderCell>
                <TableHeaderCell data-testid="desc">
                    <STitleWithIcon>
                        <Text code="lblDescription" />
                    </STitleWithIcon>
                </TableHeaderCell>
                <TableHeaderCell data-testid="no-of-chargers">
                    <Text code="lblNoOfChargers" />
                </TableHeaderCell>

                <TableHeaderCell />
            </TableRow>
        </TableHeader>
    )
}

const FirmwareList = ({
    data,
    onActivate,
    onRemove,
    onShowMore,
    onShowLess,
    showLessIsVisible,
    showMoreIsVisible,
    loading,
    showActionMenu,
}: FirmwareListProps): ReactElement => {
    const text = useText()
    const [firmwares, setFirmwares] = useState<Array<IFirmware>>([])

    useEffect(() => {
        setFirmwares(data || [])
        setIsSettingsLocked(true)
    }, [data])

    const onLoadMoreClick = () => {
        onShowMore && onShowMore()
    }

    const onHideClick = () => {
        onShowLess && onShowLess()
    }

    const [isUnlockSettingsModalVisible, setIsUnlockSettingsModalVisible] = useState(false)
    const [isSettingsLocked, setIsSettingsLocked] = useState(true)

    return (
        <WidgetLayout
            title={text('msgFWVersions')}
            headerButtons={
                onActivate && (
                    <SUnlockButtonContainer data-testid="btn-unlock">
                        <Button
                            label={text(isSettingsLocked ? 'btnUnlock' : 'btnLock')}
                            onClick={() => setIsUnlockSettingsModalVisible(true)}
                            disabled={!isSettingsLocked}
                            icon={isSettingsLocked ? <LockIcon /> : <UnlockIcon />}
                        ></Button>
                    </SUnlockButtonContainer>
                )
            }
        >
            {onActivate && (
                <SSelectFirmwareVersionInstruction data-testid="instructions">
                    <Text code="msgFWVersionInstruction" />
                </SSelectFirmwareVersionInstruction>
            )}

            <Table>
                <FirmwareModalTable />
                {!loading && (
                    <>
                        {firmwares.map((firmware) => (
                            <FirmwareListItem
                                key={firmware.id}
                                data={firmware}
                                onActivate={onActivate}
                                onRemove={onRemove}
                                isActivationDisabled={isSettingsLocked}
                                showActionMenu={showActionMenu}
                            />
                        ))}
                    </>
                )}
            </Table>

            {showMoreIsVisible && (
                <SExpandButton onClick={onLoadMoreClick} data-testid="load-more">
                    <Text code="btnLoadMore" />
                    <SExpandIcon />
                </SExpandButton>
            )}
            {showLessIsVisible && (
                <SExpandButton onClick={onHideClick} data-testid="show-less">
                    <Text code="btnHide" />
                    <SCollapseIcon />
                </SExpandButton>
            )}

            {isUnlockSettingsModalVisible && (
                <Modal>
                    <AlertModal
                        title="Unlocking factory firmware settings"
                        message="By proceeding, you are identifying yourself as an authorized user. Any changes you make in the settings will be stored in an audit log."
                        showCancel
                        dismiss={() => setIsUnlockSettingsModalVisible(false)}
                        close={() => {
                            setIsSettingsLocked(false)
                            setIsUnlockSettingsModalVisible(false)
                        }}
                        okButtonText="Unlock"
                    />
                </Modal>
            )}
        </WidgetLayout>
    )
}

export default FirmwareList
